/**
 * InputMeter: lightweight microphone level meter (no recording).
 * - Uses getUserMedia + AnalyserNode, returns a 0..1 level.
 * - Does not connect to destination (no audible monitoring).
 */

export interface InputMeterOptions {
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  channels?: number;
  inputDeviceId?: string | null;
}

export class InputMeter {
  private stream: MediaStream | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private analyserData: Uint8Array<ArrayBuffer> | null = null;

  constructor(private readonly audioContext: AudioContext, private readonly options: InputMeterOptions = {}) {}

  public get isRunning(): boolean {
    return Boolean(this.stream);
  }

  public async start(): Promise<void> {
    if (this.stream) return;

    const channels = this.options.channels ?? 1;
    const inputDeviceId = this.options.inputDeviceId ?? null;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: this.options.echoCancellation ?? false,
        noiseSuppression: this.options.noiseSuppression ?? false,
        autoGainControl: this.options.autoGainControl ?? false,
        channelCount: channels,
        ...(inputDeviceId ? { deviceId: { exact: inputDeviceId } } : {}),
      } as any,
    });

    if (this.audioContext.state === "suspended") {
      await this.audioContext.resume();
    }

    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
    this.analyserNode = this.audioContext.createAnalyser();
    // Smaller FFT = lower latency for a meter.
    this.analyserNode.fftSize = 1024;
    this.analyserNode.smoothingTimeConstant = 0.72;
    this.analyserData = new Uint8Array(new ArrayBuffer(this.analyserNode.fftSize));

    this.sourceNode.connect(this.analyserNode);
  }

  public stop(): void {
    try {
      if (this.sourceNode) this.sourceNode.disconnect();
    } catch {
      // ignore
    }
    try {
      if (this.analyserNode) this.analyserNode.disconnect();
    } catch {
      // ignore
    }

    if (this.stream) {
      try {
        this.stream.getTracks().forEach((t) => t.stop());
      } catch {
        // ignore
      }
    }

    this.stream = null;
    this.sourceNode = null;
    this.analyserNode = null;
    this.analyserData = null;
  }

  public getLevel(): number {
    if (!this.analyserNode || !this.analyserData) return 0;
    this.analyserNode.getByteTimeDomainData(this.analyserData);

    // RMS of normalized PCM.
    let sumSq = 0;
    for (let i = 0; i < this.analyserData.length; i += 1) {
      const v = (this.analyserData[i] - 128) / 128;
      sumSq += v * v;
    }
    const rms = Math.sqrt(sumSq / this.analyserData.length);

    // Gate + scale to a usable UI range.
    const gated = Math.max(0, rms - 0.02);
    const scaled = gated / 0.25;
    if (scaled <= 0) return 0;
    if (scaled >= 1) return 1;
    return scaled;
  }
}
